'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';


interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navigationItems = [
    { label: 'Home', path: '/homepage', icon: 'HomeIcon' },
    { label: 'Programs', path: '/programs-overview', icon: 'AcademicCapIcon' },
    { label: 'Career Services', path: '/career-services', icon: 'BriefcaseIcon' },
    { label: 'Internship', path: '/free-internship', icon: 'CodeBracketIcon' },
    { label: 'Enquiry', path: '/enquiry', icon: 'BuildingLibraryIcon' },
    { label: 'About', path: '/about', icon: 'InformationCircleIcon' },
  ];

  const isActivePath = (path: string) => {
    return pathname === path;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-card shadow-sky-md' : 'bg-card'
      } ${className}`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 md:px-6 lg:px-8" style={ {maxWidth: '1366px', margin: 'auto'} }>
          {/* Logo Section */}
          <Link href="/homepage" className="flex items-center space-x-3 group">
            <div className="relative w-10 h-10 transition-transform duration-300 group-hover:scale-105">
              <img src="/assets/images/ViTi.png" alt="VitiQuest logo" loading="eager" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-headline font-bold text-foreground tracking-tight">
                ViTiQuest
              </span>
              <span className="text-xs font-body text-muted-foreground -mt-1">
                Learning
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                href={item.path}
                className={`relative px-4 py-2 rounded-md font-body font-medium text-sm transition-all duration-300 ${
                  isActivePath(item.path)
                    ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-muted'
                }`}
              >
                {item.label}
                {isActivePath(item.path) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-gradient-to-r from-sky-light via-primary to-accent rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link
              href="/programs-overview"
              className="px-4 py-2 text-sm font-headline font-semibold text-foreground hover:text-primary transition-colors duration-300"
            >
              Explore Programs
            </Link>
            {/* <Link
              href="/learning-dashboard"
              className="px-6 py-2.5 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-md font-headline font-semibold text-sm shadow-sky-sm hover:shadow-sky-md transition-all duration-300 hover:scale-105"
            >
              Start Learning
            </Link> */}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-foreground hover:bg-muted transition-colors duration-300"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-card z-40">
          <nav className="flex flex-col p-4 space-y-2 h-full overflow-y-auto">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                href={item.path}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-body font-medium transition-all duration-300 ${
                  isActivePath(item.path)
                    ? 'text-primary bg-primary/10 shadow-sky-sm'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item.icon as any} size={20} variant="outline" />
                <span>{item.label}</span>
              </Link>
            ))}

            <div className="pt-4 mt-4 border-t border-border space-y-3">
              <Link
                href="/programs-overview"
                className="block w-full px-4 py-3 text-center text-foreground font-headline font-semibold border border-border rounded-lg hover:bg-muted transition-all duration-300"
              >
                Explore Programs
              </Link>

              {/* <Link
                href="/learning-dashboard"
                className="block w-full px-4 py-3 text-center bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-lg font-headline font-semibold shadow-sky-sm hover:shadow-sky-md transition-all duration-300"
              >
                Start Learning
              </Link> */}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;